import type { LiveDiagram } from "@scoach/types";
import { Button, SegmentedControl } from "@scoach/ui";
import { Image } from "@scoach/ui/icons";
import { useEffect, useState } from "react";

import { api } from "../../../../lib/http.ts";
import { useLiveMeetingStore } from "../store.ts";
import { DiagramLightbox } from "./DiagramLightbox.tsx";
import { MermaidDiagram } from "./diagrams/MermaidDiagram.tsx";

type Kind = "architecture" | "flowchart";

/** Minimum transcript lines before there's enough context to draw a useful diagram.
 * Below this the Generate button is hidden (and the tab shows no "ready" dot). */
export const DIAGRAM_MIN_CONTEXT_LINES = 6;

/**
 * Diagram panel for one kind — "architecture" (the Diagram tab) or "flowchart" (the
 * Decision tree tab). Each tab shows only its own diagrams and generates only that kind,
 * so the two never mix. Architecture keeps the clarify-questions flow; the decision tree
 * generates directly on demand. All diagrams are kept (newest first) so history stays
 * reachable; any diagram can be expanded to a full-window popup.
 */
export function LiveDiagramPanel({ kind }: { kind: Kind }) {
  const isArch = kind === "architecture";
  const label = isArch ? "diagram" : "decision tree";

  const meetingId = useLiveMeetingStore((s) => s.meetingId);
  const transcriptLen = useLiveMeetingStore((s) => s.transcript.length);
  const canGenerate = transcriptLen >= DIAGRAM_MIN_CONTEXT_LINES;
  const allDiagrams = useLiveMeetingStore((s) => s.diagrams);
  const clarificationsRaw = useLiveMeetingStore((s) => s.diagramClarifications);
  const generating = useLiveMeetingStore((s) => s.diagramGenerating);
  const markSeen = useLiveMeetingStore((s) => s.markDiagramsSeen);
  const setClarifications = useLiveMeetingStore((s) => s.setDiagramClarifications);
  const setGenerating = useLiveMeetingStore((s) => s.setDiagramGenerating);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<LiveDiagram | null>(null);
  // Diagrams that failed to render on the client — hidden, and auto-regenerated
  // (the backend now self-heals invalid Mermaid, so a retry almost always works).
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());
  const [autoRetries, setAutoRetries] = useState(0);
  const MAX_AUTO_RETRIES = 2;

  // Architecture owns the (architecture-oriented) clarify questions; the flowchart tab
  // generates directly, so it ignores them.
  const clarifications = isArch ? clarificationsRaw : null;
  const diagrams = allDiagrams.filter(
    (d) => (isArch ? d.kind !== "flowchart" : d.kind === "flowchart") && !failedIds.has(d.id),
  );

  // A diagram that failed to render on the client: hide it and auto-regenerate a
  // fresh one (backend self-heals invalid Mermaid), up to a small cap.
  function handleRenderFail(id: string) {
    setFailedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    if (autoRetries < MAX_AUTO_RETRIES && !generating) {
      setAutoRetries((n) => n + 1);
      void add();
    }
  }

  useEffect(() => {
    markSeen();
  }, [markSeen]);

  useEffect(() => {
    if (!clarifications) return;
    setAnswers((prev) => {
      const next = { ...prev };
      for (const q of clarifications) {
        if (next[q.id] === undefined) {
          const rec = q.options.find((o) => o.recommended) ?? q.options[0];
          if (rec) next[q.id] = rec.value;
        }
      }
      return next;
    });
  }, [clarifications]);

  // Architecture: run the readiness/clarify gate. Flowchart: generate directly.
  async function generate() {
    if (!meetingId) return;
    setGenerating(true);
    try {
      if (isArch) {
        const res = await api<{ ready: boolean; questions?: typeof clarifications }>(
          `/meetings/${meetingId}/diagram/clarify`,
          { method: "POST" },
        );
        if (!res.ready && res.questions && res.questions.length > 0)
          setClarifications(res.questions);
      } else {
        await api(`/meetings/${meetingId}/diagram/add`, {
          method: "POST",
          body: { kinds: ["flowchart"] },
        });
      }
    } catch {
      /* swallow — button re-enables below */
    } finally {
      setGenerating(false);
    }
  }

  // Explicit "Add another" — always appends a fresh diagram of this kind.
  async function add() {
    if (!meetingId) return;
    setGenerating(true);
    try {
      await api(`/meetings/${meetingId}/diagram/add`, {
        method: "POST",
        body: { kinds: [kind] },
      });
    } catch {
      /* swallow */
    } finally {
      setGenerating(false);
    }
  }

  async function submitAnswers() {
    if (!meetingId || !clarifications) return;
    const payload = clarifications.map((q) => {
      const val = answers[q.id];
      const opt = q.options.find((o) => o.value === val);
      return { question: q.text, answer: opt?.label ?? val ?? "" };
    });
    setGenerating(true);
    try {
      await api(`/meetings/${meetingId}/diagram/generate`, {
        method: "POST",
        body: { answers: payload },
      });
      setClarifications(null);
    } catch {
      /* swallow */
    } finally {
      setGenerating(false);
    }
  }

  const hasQuestions = !!clarifications && clarifications.length > 0;

  // Clarification questions take over the panel until answered (architecture only).
  if (hasQuestions) {
    return (
      <div className="infographic-panel" style={{ minHeight: 0 }}>
        <div
          className="scroll"
          style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 16 }}
        >
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>
            A couple of quick questions so the diagram is accurate — the recommended answer is
            pre-selected.
          </div>
          {clarifications?.map((q) => (
            <div key={q.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{q.text}</div>
              {q.hint && <div style={{ fontSize: 11, color: "var(--text-4)" }}>{q.hint}</div>}
              <SegmentedControl
                size="sm"
                variant="block"
                ariaLabel={q.text}
                value={answers[q.id] ?? ""}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
                options={q.options.map((o) => ({
                  value: o.value,
                  label: o.recommended ? `${o.label} (recommended)` : o.label,
                }))}
              />
            </div>
          ))}
          <div>
            <Button onClick={submitAnswers} disabled={generating}>
              {generating ? "Generating…" : "Generate diagram"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (diagrams.length === 0) {
    return (
      <div className="ig-empty">
        <Image size={28} color="var(--text-4)" />
        <div className="ig-empty-title">{isArch ? "Diagram" : "Decision tree"}</div>
        <div className="ig-empty-sub">
          {!canGenerate
            ? "Waiting for a bit more of the conversation before there's enough to draw from…"
            : isArch
              ? "There's enough context now — generate an architecture diagram of the system being discussed."
              : "There's enough context now — generate a decision-tree / flowchart of the logic or process discussed."}
        </div>
        {canGenerate && (
          <div style={{ marginTop: 12 }}>
            <Button onClick={() => void generate()} disabled={generating}>
              {generating ? "Working…" : isArch ? "Generate diagram" : "Generate decision tree"}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="infographic-panel" style={{ minHeight: 0 }}>
        <div
          className="scroll"
          style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 14 }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>
              {diagrams.length} {label}
              {diagrams.length === 1 ? "" : "s"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button size="sm" onClick={() => void add()} disabled={generating}>
                {generating ? "Working…" : isArch ? "Add diagram" : "Add decision tree"}
              </Button>
              {isArch && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void generate()}
                  disabled={generating}
                >
                  Regenerate
                </Button>
              )}
            </div>
          </div>
          {diagrams.map((d) => (
            <div
              key={d.id}
              style={{
                border: "1px solid var(--border, #e0e0e0)",
                borderRadius: 8,
                padding: "10px 12px",
                background: "var(--surface, #fff)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  #{d.index} · {d.title}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setExpanded(d)}>
                  Expand
                </Button>
              </div>
              <MermaidDiagram chart={d.mermaid} zoomable onError={() => handleRenderFail(d.id)} />
            </div>
          ))}
        </div>
      </div>
      <DiagramLightbox diagram={expanded} onClose={() => setExpanded(null)} />
    </>
  );
}
